import {
    collection,
    doc,
    getDoc,
    query,
    setDoc,
    updateDoc,
    getDocs,
    where,
    writeBatch,
    Timestamp,
    serverTimestamp,
    onSnapshot,
    WriteBatch,
    deleteField
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../firebaseConfig';
import type { LedgerBatchResult } from './ledgerServices';
import { FetchState, NamedEntity, OptionalTransactionType, Plan, RecurrenceFrequency, RecurringTransactionRule, ReferralDetails, ReferralReward, ReferralRewardStatus, ReferralRewardType, SpendingAllocation, TransactionSource, TransactionStatus, TransactionType, User, UserSetting } from '../global/types';
import { useEffect, useState } from 'react';
import { generateReferralCode } from '../utils/stringUtils';
import { deleteUser } from 'firebase/auth';
import { v4 } from 'uuid';
import logger from '../utils/logger';
import { SetupWizardInformations } from '../components/SetupWizard';
import _ from 'lodash';
import { getCategoryIdByName } from '../global/categories';

export function updateUserData(id: string, data: Partial<User>): Promise<void> {
    return updateDoc(doc(db, 'users', id), data);
}

export function updateUserSettings(id: string, data: Partial<UserSetting>): Promise<void> {
    return updateDoc(doc(db, 'settings', id), data);
}

/**
 * TODO(T18 cleanup): delete this function and its call sites once no settings doc carries toolsCurrency any more.
 */
export function cleanupReportingCurrency(userId: string, code: string): Promise<void> {
    return updateDoc(doc(db, 'settings', userId), { currency: code, toolsCurrency: deleteField() });
}

export function updateUserLanguage(id: string | undefined, language: string): Promise<void> | undefined {
    if (!id) return;
    return updateDoc(doc(db, 'settings', id), { language });
}

export function useFetchUser(id: string | null): FetchState<User> {
    const [state, setState] = useState<FetchState<User>>({
        data: null,
        loading: !!id,
        error: null
    });

    useEffect(() => {
        if (!id) {
            setState({ data: null, loading: false, error: null });
            return;
        }

        const fetchUser = async () => {
            try {
                const userDoc = await getDoc(doc(db, 'users', id));
                setState({
                    data: userDoc.exists() ? userDoc.data() as User : null,
                    loading: false,
                    error: null
                });
            } catch (err) {
                setState({ data: null, loading: false, error: 'Failed to fetch user data' });
            }
        };

        fetchUser();
    }, [id]);

    return state;
}

export function useFetchUserSettings(id: string | null): FetchState<UserSetting> {
    const [data, setData] = useState<UserSetting | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!id) {
            setData(null);
            setLoading(false);
            return;
        }

        const fetchUserSettings = async () => {
            try {
                const settingsDoc = await getDoc(doc(db, 'settings', id));

                if (settingsDoc.exists() && settingsDoc.data()) {
                    setData(settingsDoc.data() as UserSetting);
                } else {
                    setData(null);
                }
            } catch (err) {
                console.error('Error fetching user settings:', err);
                setError('Failed to fetch user settings');
            } finally {
                setLoading(false);
            }
        };

        setLoading(true);
        fetchUserSettings()
            .catch((err) => {
                console.error('Error in fetchUserSettings:', err);
                setError('Failed to fetch user settings');
            })
            .finally(() => {
                setLoading(false);
            });

    }, [id]);

    return { data, loading, error };
}

export async function checkRefferalCodeValidity(code: string): Promise<boolean> {
    try {
        const q = query(collection(db, 'referralDetails'), where('referralCode', '==', code));
        const snapshot = await getDocs(q);
        return !snapshot.empty;
    } catch (error) {
        console.error('Error checking referral code validity:', error);
        return false;
    }
}

export async function getReferralOwner(code: string): Promise<string | null> {
    try {
        const q = query(collection(db, 'referralDetails'), where('referralCode', '==', code));
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
            return snapshot.docs[0].id;
        }
        return null;
    } catch (error) {
        console.error('Error getting referral owner:', error);
        return null;
    }
}

export function useFetchReferralCode(userId: string | null): FetchState<ReferralDetails> {
    const [state, setState] = useState<FetchState<ReferralDetails>>({
        data: null,
        loading: !!userId,
        error: null
    });

    useEffect(() => {
        if (!userId) {
            setState({ data: null, loading: false, error: null });
            return;
        }

        const fetchReferralCode = async () => {
            try {
                const docRef = doc(db, 'referralDetails', userId);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setState({
                        data: docSnap.data() as ReferralDetails,
                        loading: false,
                        error: null
                    });
                } else {
                    const details: ReferralDetails = {
                        referralCode: generateReferralCode(userId),
                        referredBy: null
                    };
                    await setDoc(docRef, details);
                    setState({
                        data: details,
                        loading: false,
                        error: null
                    });
                }
            } catch (error) {
                console.error('Error fetching referral code:', error);
                setState({ data: null, loading: false, error: 'Failed to fetch referral code' });
            }
        };

        fetchReferralCode();
    }, [userId]);

    return state;
}

export function useFetchUserRewards(userId: string | null): FetchState<ReferralReward[]> {
    const [state, setState] = useState<FetchState<ReferralReward[]>>({
        data: null,
        loading: !!userId,
        error: null
    });

    useEffect(() => {
        if (!userId) {
            setState({ data: null, loading: false, error: null });
            return;
        }

        const q = query(collection(db, 'referralRewards'), where('referrerId', '==', userId));
        const unsubscribe = onSnapshot(q,
            (snapshot) => {
                const rewards = snapshot.docs.map(doc => doc.data() as ReferralReward);
                setState({ data: rewards, loading: false, error: null });
            },
            (error) => {
                console.error('Error fetching user rewards:', error);
                setState({ data: null, loading: false, error: error.message });
            }
        );

        return () => unsubscribe();
    }, [userId]);

    return state;
}

export function useFetchReferredUsers(referralCode: string | undefined): FetchState<string[]> {
    const [state, setState] = useState<FetchState<string[]>>({
        data: null,
        loading: !!referralCode,
        error: null
    });

    useEffect(() => {
        if (!referralCode) {
            setState({ data: null, loading: false, error: null });
            return;
        }

        const fetchReferredUsers = async () => {
            try {
                const q = query(collection(db, 'referralDetails'), where('referredBy', '==', referralCode));
                const querySnapshot = await getDocs(q);
                const uids = querySnapshot.docs.map(doc => doc.id);
                setState({
                    data: uids,
                    loading: false,
                    error: null
                });
            } catch (error) {
                console.error('Error fetching referred users:', error);
                setState({ data: null, loading: false, error: 'Failed to fetch referred users' });
            }
        };

        fetchReferredUsers();
    }, [referralCode]);

    return state;
}

export function useFetchMultipleUsers(uids: string[] | null): FetchState<User[]> {
    const [state, setState] = useState<FetchState<User[]>>({
        data: null,
        loading: !!uids && uids.length > 0,
        error: null
    });

    useEffect(() => {
        if (!uids || uids.length === 0) {
            setState({ data: [], loading: false, error: null });
            return;
        }

        const fetchUsers = async () => {
            try {
                const users: User[] = [];
                // Firestore 'in' query supports up to 10 items
                const chunks = _.chunk(uids, 10);
                for (const chunk of chunks) {
                    const q = query(collection(db, 'users'), where('id', 'in', chunk));
                    const querySnapshot = await getDocs(q);
                    querySnapshot.forEach(doc => {
                        users.push(doc.data() as User);
                    });
                }
                setState({
                    data: users,
                    loading: false,
                    error: null
                });
            } catch (error) {
                console.error('Error fetching users:', error);
                setState({ data: null, loading: false, error: 'Failed to fetch users' });
            }
        };

        fetchUsers();
    }, [uids]);

    return state;
}

export async function deleteUserData(userId: string): Promise<boolean> {
    const settingCollection = 'settings';
    const settingsSubCollections = ['income', 'expenses', 'debts', 'subscriptions', 'bills', 'savings', 'spenders', 'transfers'];
    const collectionsHavingId = [
        'bufferBudgets', 'referralDetails', 'subCategoryBudgets',
        'subscriptions', 'users', 'telegramChats'
    ];
    const collectionsUsingId = [
        'accountTransfers', 'accounts', 'emotionalSpendings', 'goalsMatrix',
        'habits', 'informalDebts', 'orders', 'recTransactionSetups', 'recTransactions',
        'variableTransactions', 'subscriptionTrackerRecords'
    ];

    const batch = writeBatch(db);

    const recTransactionSetupsQuery = query(collection(db, 'recTransactionSetups'), where('userId', '==', userId));
    const recTransactionSetupsSnapshot = await getDocs(recTransactionSetupsQuery);
    await Promise.all(recTransactionSetupsSnapshot.docs.map(async (document) => {
        const recTransactionSetupId = document.id;
        const recTransactionsQuery = query(collection(db, 'recTransactions'), where('setupId', '==', recTransactionSetupId));
        const recTxSnapshot = await getDocs(recTransactionsQuery);
        recTxSnapshot.forEach(txDoc => {
            batch.delete(doc(db, 'recTransactions', txDoc.id));
        });
        batch.delete(doc(db, 'recTransactionSetups', recTransactionSetupId));
    }));

    const recurringRulesQuery = query(collection(db, 'recurringTransactionRules'), where('userId', '==', userId));
    const recurringRulesSnapshot = await getDocs(recurringRulesQuery);
    await Promise.all(recurringRulesSnapshot.docs.map(async (document) => {
        const ruleId = document.id;
        const ruleLedgerTxQuery = query(collection(db, 'ledger_transactions'), where('recurringRuleId', '==', ruleId));
        const ruleLedgerTxSnapshot = await getDocs(ruleLedgerTxQuery);
        ruleLedgerTxSnapshot.forEach(txDoc => {
            batch.delete(doc(db, 'ledger_transactions', txDoc.id));
        });
        batch.delete(doc(db, 'recurringTransactionRules', ruleId));
    }));

    for (const subCollection of settingsSubCollections) {
        const subCollectionRef = collection(db, settingCollection, userId, subCollection);
        const subCollectionSnapshot = await getDocs(subCollectionRef);
        subCollectionSnapshot.forEach(doc => {
            batch.delete(doc.ref);
        });
    }
    batch.delete(doc(db, settingCollection, userId));

    for (const collectionName of collectionsUsingId) {
        const querySnapshot = await getDocs(query(collection(db, collectionName), where('userId', '==', userId)));
        querySnapshot.forEach(doc => {
            batch.delete(doc.ref);
        });
    }

    for (const collectionName of collectionsHavingId) {
        batch.delete(doc(db, collectionName, userId));
    }

    try {
        await batch.commit();
        await deleteUser(auth.currentUser!);
        return true;
    } catch (error) {
        console.error('Error deleting user data:', error);
        return false;
    }
}

const commitLedgerTransactionsBatchFn = httpsCallable<{ rows: Record<string, unknown>[] }, LedgerBatchResult>(
    functions,
    'commitLedgerTransactionsBatch'
);

export async function applyWizardSetup({ goals, method, expenses, region, income }: SetupWizardInformations, userId: string) {
    const batch = writeBatch(db);
    const settingRef = doc(db, "settings", userId);
    const now = Timestamp.now();

    for (const goal of goals) {
        const id = v4();
        batch.set(doc(db, "goals", id), {
            id,
            name: goal
        });
    }

    batch.set(settingRef, {
        method,
        currency: region.currency?.code,
        region: region.region,
        // TODO(T18 cleanup): delete this line with the rest of the toolsCurrency handling.
        toolsCurrency: deleteField()
    }, { merge: true });

    if (region.currency?.code) {
        batch.set(doc(db, "accounts", `cash-${userId}`), {
            currency: region.currency.code
        }, { merge: true });
    }

    const incomeCategories = ["Base Salary", ..._.map(income.others, "name")];
    const filteredExpenses = (_.filter(expenses.mains, "amount") as SetupWizardInformations["expenses"]["others"])
        .concat(expenses.others);
    const expensesCategories = _.map(filteredExpenses, "name");
    const categoriesMap: Record<string, string> = {};
    for (const category of incomeCategories) {
        const id = getCategoryIdByName(category);
        if (!id) throw new Error(`Income category "${category}" not found in predefined categories.`);

        categoriesMap[category] = id;
        batch.set(doc(db, "settings", userId, "income", id), {
            id,
            name: category
        });
    }
    for (const category of expensesCategories) {
        const id = getCategoryIdByName(category);
        if (!id) throw new Error(`Income category "${category}" not found in predefined categories.`);

        categoriesMap[category] = id;
        batch.set(doc(db, "settings", userId, "expenses", id), {
            id,
            name: category
        });
    }

    const earnerOrSpenderId = v4();
    batch.set(doc(db, "settings", userId, "spenders", earnerOrSpenderId), {
        id: earnerOrSpenderId,
        name: "Me"
    });

    if (income.amount) {
        const base: RecurringTransactionRule = {
            id: v4(),
            userId,
            frequency: RecurrenceFrequency.MONTHLY,
            interval: 1,
            startDate: now,
            endDate: null,
            template: {
                amount: income.amount,
                currency: region.currency?.code || 'USD',
                type: TransactionType.INCOME,
                categoryId: categoriesMap["Base Salary"],
                categoryGroup: TransactionType.INCOME,
                accountId: `cash-${userId}`,
                spenderOrEarnerId: earnerOrSpenderId,
                description: '',
                distribution: null,
                isBeforeTax: true,
                taxRate: 0,
            },
            isActive: true,
            createdAt: now,
            updatedAt: now
        };
        logger.debug("Base salary rule:", base);
        batch.set(doc(db, "recurringTransactionRules", base.id), base);
    }
    for (const inc of income.others) {
        if (!inc.amount) continue;
        logger.debug("Other income rule:", inc);
        const data: RecurringTransactionRule = {
            id: v4(),
            userId,
            frequency: RecurrenceFrequency.MONTHLY,
            interval: 1,
            startDate: now,
            endDate: null,
            template: {
                amount: inc.amount,
                currency: region.currency?.code || 'USD',
                type: TransactionType.INCOME,
                categoryId: categoriesMap[inc.name],
                categoryGroup: TransactionType.INCOME,
                accountId: `cash-${userId}`,
                spenderOrEarnerId: earnerOrSpenderId,
                description: '',
                distribution: null,
                isBeforeTax: true,
                taxRate: 0,
            },
            isActive: true,
            createdAt: now,
            updatedAt: now
        };
        batch.set(doc(db, "recurringTransactionRules", data.id), data);
    }

    await batch.commit();

    const expenseRows = filteredExpenses
        .filter((expense) => !!expense.amount)
        .map((expense) => ({
            accountId: `cash-${userId}`,
            amount: expense.amount,
            date: now.toMillis(),
            type: TransactionType.EXPENSE,
            categoryId: categoriesMap[expense.name],
            categoryGroup: OptionalTransactionType.EXPENSES,
            spenderOrEarnerId: earnerOrSpenderId,
            description: '',
            status: TransactionStatus.POSTED,
            source: TransactionSource.MANUAL,
        }));

    if (expenseRows.length > 0) {
        const { data } = await commitLedgerTransactionsBatchFn({ rows: expenseRows });
        if (data.rejected.length > 0) {
            logger.warn('Some onboarding expenses were refused', data.rejected);
        }
    }
}

export async function getUserByEmail(email: string): Promise<User | null> {
    const q = query(collection(db, 'users'), where('email', '==', email));
    const querySnapshot = await getDocs(q);
    if (!querySnapshot.empty) {
        return querySnapshot.docs[0].data() as User;
    }
    return null;
}

export async function fetchAllFinancialCategories(
    userId: string
): Promise<(NamedEntity & { category: string })[]> {
    const collections = ["income", "debts", "subscriptions", "bills", "expenses", "savings", "investments", "guilt_free"];

    const results = await Promise.all(
        collections.map(async (collectionName) => {
            const snapshot = await getDocs(collection(db, "settings", userId, collectionName));
            return snapshot.docs.map((doc) => ({
                ...(doc.data() as NamedEntity),
                id: doc.id,
                category: collectionName.toUpperCase(),
            }));
        })
    );

    return results.flat();
}

export async function fetchUserSettings(userId: string): Promise<UserSetting | null> {
    const docSnap = await getDoc(doc(db, "settings", userId));
    return docSnap.exists() ? docSnap.data() as UserSetting : null;
}

export async function deleteAndArchiveSubCategory(userId: string, category: string, subCategoryId: string): Promise<void> {
    const subCategoryRef = doc(db, "settings", userId, category, subCategoryId);

    const getOnce = <T = any>(ref: any): Promise<T> =>
        new Promise((resolve, reject) => {
            const unsub = onSnapshot(
                ref,
                (snap: any) => {
                    unsub();
                    resolve(snap);
                },
                (err) => {
                    reject(err);
                }
            );
        });

    try {
        const [
            subCategorySnap,
            individualSpendingPlans,
            recTransactionSetups,
            recTransactions,
            recurringRules,
            subCategoryBudgets,
            variableTransactions
        ] = await Promise.all([
            getOnce(subCategoryRef),
            getOnce(doc(db, "individualSpendingPlans", userId)),
            getOnce(query(collection(db, "recTransactionSetups"), where("subCategoryId", "==", subCategoryId))),
            getOnce(query(collection(db, "recTransactions"), where("subCategoryId", "==", subCategoryId))),
            getOnce(query(collection(db, "recurringTransactionRules"), where("template.categoryId", "==", subCategoryId))),
            getOnce(query(collection(db, "subCategoryBudgets"), where("subCategoryId", "==", subCategoryId))),
            getOnce(query(collection(db, "variableTransactions"), where("subCategoryId", "==", subCategoryId)))
        ]);

        const recTransactionsArchiveRef = collection(db, "recTransactionsArchive");
        const recTransactionSetupsArchiveRef = collection(db, "recTransactionSetupsArchive");
        const recurringRulesArchiveRef = collection(db, "recurringTransactionRulesArchive");
        const subCategoryBudgetsArchiveRef = collection(db, "subCategoryBudgetsArchive");
        const variableTransactionsArchiveRef = collection(db, "variableTransactionsArchive");

        const batches: WriteBatch[] = [];
        let currentBatch = writeBatch(db);
        let opCount = 0;

        const pushOp = (op: () => void) => {
            if (opCount >= 490) { // 490 as safe threshold
                batches.push(currentBatch);
                currentBatch = writeBatch(db);
                opCount = 0;
            }
            op();
            opCount++;
        };

        const now = Timestamp.now();

        // Delete original if exists
        if (subCategorySnap.exists && subCategorySnap.exists()) {
            pushOp(() => currentBatch.delete(subCategoryRef));
        }

        // To delete the budget allocation including the category
        if (individualSpendingPlans.exists && individualSpendingPlans.exists()) {
            const data = individualSpendingPlans.data();
            if (data && data.spendingAllocations) {
                const updatedAllocations = data.spendingAllocations.filter((alloc: any) => alloc.subCategoryId !== subCategoryId);
                pushOp(() => currentBatch.update(doc(db, "individualSpendingPlans", userId), { spendingAllocations: updatedAllocations }));
            }
        }

        const archiveDocsFromSnapshot = (snap: any, archiveColRef: any, origCollectionName: string) => {
            if (!snap || !snap.docs) return;
            for (const d of snap.docs) {
                const dataWithMeta = { ...(d.data() as object), id: d.id, originalCollection: origCollectionName, archivedAt: now };
                const archiveDocRef = doc(archiveColRef);
                const origDocRef = doc(db, origCollectionName, d.id);
                pushOp(() => currentBatch.set(archiveDocRef, dataWithMeta));
                pushOp(() => currentBatch.delete(origDocRef));
            }
        };

        archiveDocsFromSnapshot(recTransactionSetups, recTransactionSetupsArchiveRef, "recTransactionSetups");
        archiveDocsFromSnapshot(recTransactions, recTransactionsArchiveRef, "recTransactions");
        archiveDocsFromSnapshot(recurringRules, recurringRulesArchiveRef, "recurringTransactionRules");
        archiveDocsFromSnapshot(subCategoryBudgets, subCategoryBudgetsArchiveRef, "subCategoryBudgets");
        archiveDocsFromSnapshot(variableTransactions, variableTransactionsArchiveRef, "variableTransactions");

        batches.push(currentBatch);

        // Commit all batches
        for (const b of batches) {
            await b.commit();
        }
    } catch (error) {
        console.error("Error fetching subcategory data:", error);
    }
}
